import JourneyParameters from '@/models/interfaces/JourneyParameters';
import Instance from './interfaces/Instance';
import ClassItem from './interfaces/ClassItem';
import ClassItemRate from './interfaces/ClassItemRate';
import SurveyStatus from './enums/SurveyStatus';
import Source from '@/services/Source';
import Indicators, { Indicator, Rating, Type } from '@solucx/indicators';
import { AcceptanceChannels } from './ParamsData';

/**
 Objeto que representa a avaliação. Este é enviado para o backend durante a submissão de avaliação completa ou parcial
 */
export default class SurveyAnswer {
  public transactionId?: string;
  public journeyId?: number;
  public journeyName?: string;
  public journeyType?: number;
  public attemptId?: string;
  public storeId?: string;
  public employeeId?: string;
  public score?: number;
  public instance: Instance;
  public comment?: string;
  public items?: ClassItemRate[];
  public journeyParameters?: JourneyParameters;
  public anonymous?: boolean;
  public acceptanceContactIfDetractor?: boolean;
  public acceptanceContactIfNeutral?: boolean;
  public acceptanceContactIfPromoter?: boolean;
  public source?: Source;
  public isBlockedByCookieQuarantine?: boolean;
  public acceptanceChannels?: AcceptanceChannels;

  constructor(
    transactionId: string = null,
    attemptId: string = null,
    storeId: string = null,
    employeeId: string = null,
    score: string = null,
    classes: ClassItem[] = null,
    journeyType: number = null,
    comment: string = null,
  ) {
    this.transactionId = transactionId;
    this.attemptId = attemptId;
    this.storeId = storeId;
    this.employeeId = employeeId;
    this.score = score ? parseInt(score) : null;
    this.comment = comment;
    this.anonymous = null;
    this.acceptanceContactIfDetractor = false;
    this.acceptanceContactIfNeutral = false;
    this.acceptanceContactIfPromoter = false;
    this.items = [];
    this.isBlockedByCookieQuarantine = null;
    this.acceptanceChannels = {
      email: false,
      whatsapp: false,
      phone: false,
    };

    if (classes) this.items = classes?.map((i) => ({ classId: i.idClass, like: null, score: null, type: null }));
    if (journeyType !== null || journeyType !== undefined) this.journeyType = journeyType;
  }

  public get isJourneyTypeNone() {
    return this.journeyType === Type.NONE;
  }

  /**
   * Return if a survey response is valid
   * TODO: colocar em um só
   */
  public getSurveyStatus(): SurveyStatus {
    if (this.transactionId == null) return SurveyStatus.surveyInvalid;
    if (this.attemptId == null) return SurveyStatus.surveyInvalid;
    if (this.score == null) return SurveyStatus.scoreMissing;
    if (this.items)
      for (const item of this.items)
        if (
          item.like == null &&
          item.score == null &&
          this.journeyParameters.classesNotRequired === false &&
          item.type !== 'optionalLikeDislike'
        ) {
          return SurveyStatus.itemsMissing;
        }
    return SurveyStatus.valid;
  }

  /**
   * Gets survey status for totem
   * @returns survey status totem
   * TODO: colocar em um só
   */
  public getSurveyStatusTotem(): SurveyStatus {
    if (this.storeId == null) return SurveyStatus.surveyInvalid;
    if (this.score == null) return SurveyStatus.scoreMissing;
    if (this.items)
      for (const item of this.items)
        if (
          item.like == null &&
          this.journeyParameters.classesNotRequired === false &&
          item.type !== 'optionalLikeDislike'
        )
          return SurveyStatus.itemsMissing;
    return SurveyStatus.valid;
  }

  /**
   * Return if a survey response is valid
   * TODO: colocar em um só
   */
  public getSurveyStatusTotem2(): SurveyStatus {
    if (this.transactionId == null) return SurveyStatus.surveyInvalid;
    if (this.attemptId == null) return SurveyStatus.surveyInvalid;
    if (this.storeId == null) return SurveyStatus.surveyInvalid;
    if (this.score == null) return SurveyStatus.scoreMissing;
    if (this.items) for (const item of this.items) if (item.like == null) return SurveyStatus.itemsMissing;
    return SurveyStatus.valid;
  }

  public setClasses(classes: ClassItem[]): void {
    classes.forEach((i) => {
      this.items.push({ classId: i.idClass, like: null });
    });
  }

  public getRating(): Rating {
    const kpiInstance: Indicator = Indicators.getIndicator(this.journeyType);
    return kpiInstance.getRating(this.score);
  }

  public setAcceptanceContactDefaultValue(parameters: JourneyParameters): void {
    const isNegative = this.getRating() === Rating.NEGATIVE;
    const isNeutral = this.getRating() === Rating.NEUTRAL;
    const isPositive = this.getRating() === Rating.POSITIVE;

    const setAcceptanceContact = (classificationCondition: boolean, showAcceptance: boolean) =>
      classificationCondition && showAcceptance ? parameters.isUserConsentPreChecked || false : null;

    this.acceptanceContactIfDetractor = setAcceptanceContact(isNegative, parameters.showAcceptanceContactIfDetractor);
    this.acceptanceContactIfNeutral = setAcceptanceContact(isNeutral, parameters.showAcceptanceContactIfNeutral);
    this.acceptanceContactIfPromoter = setAcceptanceContact(isPositive, parameters.showAcceptanceContactIfPromoter);
  }

  public setDefaultValueBasedOnJourneyType(): void {
    if (this.isJourneyTypeNone) {
      this.score = 0;
    }
  }
}
